import { useT } from "@transifex/react";
import { useEffect, useRef, useState } from "react";
import { ButtonColorVariant, ButtonVariant, AreaButton } from "../../complex/teacherArea/AreaButton";
import { Headline } from "../../complex/teacherArea/Headline";
import { COOKIE_ACTIONS, COOKIE_TYPES, CookieActionsType, CookieType } from "../../enums/cookies";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { CookieService } from "../../services/cookie.service";
import { CookieCard } from "./CookieCard";

export const CookiePrefsModal = () => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t = useT();
    const modalRef = useRef<HTMLDialogElement>(null);
    const cardsContainerRef = useRef<HTMLDivElement>(null);
    const actionContainerRef = useRef<HTMLDivElement>(null);

    const [activeTypes, setActiveTypes] = useState<CookieType[]>([]);

    useEffect(() => {
        CookieService.getInstance().registerPrefsModal(modalRef.current);
    }, []);

    const t_cookiePrefs = t("Cookie Preferences", { _tags: "TeacherArea" });
    const t_headline = t("Personal cookie processing", { _tags: "TeacherArea" });
    const t_description = t("Select which cookies described below this website may use:", {
        _tags: "TeacherArea",
    });
    const t_requiredCookies = t("Required Cookies", { _tags: "TeacherArea" });
    const t_requiredCookiesDescription = t("Cannot be deactivated without limiting the functionality of the website", {
        _tags: "TeacherArea",
    });
    const t_analyticCookies = t("Analytic Cookies", { _tags: "TeacherArea" });
    const t_analyticCookiesDescription = t(
        "Enable anonymous evaluation of user data for the purpose of improving the offer",
        { _tags: "TeacherArea" }
    );
    const t_marketingCookies = t("Marketing Cookies", { _tags: "TeacherArea" });
    const t_marketingCookiesDescription = t("Enable measurement of the success of advertising measures", {
        _tags: "TeacherArea",
    });
    const t_socialCookies = t("Social Cookies", { _tags: "TeacherArea" });
    const t_socialCookiesDescription = t("Enable sharing on social networks and advertising via them", {
        _tags: "TeacherArea",
    });
    const t_acceptSelected = t("Confirm Selection", { _tags: "TeacherArea" });
    const t_acceptAll = t("Accept All", { _tags: "TeacherArea" });

    const cookieConfigs = [
        { type: COOKIE_TYPES.REQUIRED, title: t_requiredCookies, description: t_requiredCookiesDescription },
        { type: COOKIE_TYPES.ANALYTICS, title: t_analyticCookies, description: t_analyticCookiesDescription },
        { type: COOKIE_TYPES.MARKETING, title: t_marketingCookies, description: t_marketingCookiesDescription },
        { type: COOKIE_TYPES.SOCIAL, title: t_socialCookies, description: t_socialCookiesDescription },
    ];

    const handleCardClick = (type: CookieType) => {
        if (type === COOKIE_TYPES.REQUIRED) return; // Ignore clicks on required cookies

        setActiveTypes((prevTypes) =>
            prevTypes.includes(type) ? prevTypes.filter((t) => t !== type) : [...prevTypes, type]
        );
    };

    const handleSaveSelected = () => {
        const settings = [...activeTypes];

        const actionButtons = actionContainerRef.current?.querySelectorAll("button");

        document.dispatchEvent(
            new CustomEvent("cookies:saveSelected", {
                detail: { settings, actionButtons: Array.from(actionButtons || []) },
            })
        );
    };

    const handleAcceptAll = () => {
        const actionButtons = actionContainerRef.current?.querySelectorAll("button");
        document.dispatchEvent(
            new CustomEvent("cookies:saveAll", {
                detail: { actionButtons: Array.from(actionButtons || []) },
            })
        );
    };

    const handleActionClick = (action: CookieActionsType) => {
        switch (action) {
            case COOKIE_ACTIONS.ACCEPT_SELECTED:
                handleSaveSelected();
                break;
            case COOKIE_ACTIONS.ACCEPT_ALL:
                handleAcceptAll();
                break;
            default:
                console.warn(`Unknown cookie action: ${action}`);
        }
    };

    useEffect(() => {
        const setInitialState = () => {
            const initialActiveTypes = window.cookieService?.getActiveCookieTypes() || [];
            if (!initialActiveTypes.includes(COOKIE_TYPES.REQUIRED)) {
                initialActiveTypes.push(COOKIE_TYPES.REQUIRED);
            }
            setActiveTypes(initialActiveTypes);
        };

        document.addEventListener("cookies:serviceReady", setInitialState);
        if (window.cookieService) {
            setInitialState();
        }

        return () => document.removeEventListener("cookies:serviceReady", setInitialState);
    }, []);

    return (
        <dialog
            id="cookie-prefs-modal"
            ref={modalRef}
            className="w-11/12 max-w-4xl self-center justify-self-center rounded-lg border-none bg-white"
        >
            <article
                className="p-s gap-m flex flex-col items-center"
                aria-label={t_cookiePrefs}
            >
                <section className="flex flex-col items-center">
                    <Headline
                        level={2}
                        className="text-stone-1000 text-xxl"
                    >
                        {t_headline}
                    </Headline>
                    <p className="text-m text-stone-1000">{t_description}</p>
                </section>
                <section
                    id="cookie-cards-container"
                    ref={cardsContainerRef}
                    className={`gap-xxs grid ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "grid-cols-4",
                        getStyleBasedOnBreakpoint(Breakpoint.SM, "grid-cols-2", "grid-cols-1")
                    )}`}
                >
                    {cookieConfigs.map((c, index) => (
                        <CookieCard
                            key={c.type}
                            type={c.type}
                            title={c.title}
                            description={c.description}
                            index={index}
                            total={cookieConfigs.length}
                            isActive={activeTypes.includes(c.type)}
                            onClick={() => handleCardClick(c.type as CookieType)}
                        />
                    ))}
                </section>
                <div
                    id="prefs-action-container"
                    ref={actionContainerRef}
                    className={`gap-xs flex ${getStyleBasedOnBreakpoint(Breakpoint.SM, "flex-row", "flex-col")}`}
                >
                    <AreaButton
                        buttonId="prefs-accept-selected-btn"
                        variant={ButtonVariant.SECONDARY}
                        colorVariant={ButtonColorVariant.LUMINA}
                        role="button"
                        data-cookie-action={COOKIE_ACTIONS.ACCEPT_SELECTED}
                        onClick={() => handleActionClick(COOKIE_ACTIONS.ACCEPT_SELECTED)}
                    >
                        {t_acceptSelected}
                    </AreaButton>
                    <AreaButton
                        buttonId="prefs-accept-all-btn"
                        variant={ButtonVariant.PRIMARY}
                        colorVariant={ButtonColorVariant.LUMINA}
                        role="button"
                        data-cookie-action={COOKIE_ACTIONS.ACCEPT_ALL}
                        onClick={() => handleActionClick(COOKIE_ACTIONS.ACCEPT_ALL)}
                    >
                        {t_acceptAll}
                    </AreaButton>
                </div>
            </article>
        </dialog>
    );
};
